import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Languages, Menu, X, User, LogOut, LayoutDashboard, UserCircle } from "lucide-react";
import logoAlbaker from "@/assets/logo-albaker.png";

const Header = () => {
  const { t, lang, toggleLang, isRTL } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false);
    };
    if (menu) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menu]);

  const links = [
    { href: "#about", label: t.nav.about },
    { href: "#media", label: t.nav.media },
    { href: "#timeline", label: t.nav.timeline },
    { href: "#articles", label: t.nav.articles },
    { href: "#contact", label: t.nav.contact },
  ];

  const onLogout = async () => {
    setMenu(false);
    await logout();
    navigate("/", { replace: true });
  };

  const initials = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();
  const labelLogin = isRTL ? "تسجيل الدخول" : "Sign in";
  const labelProfile = isRTL ? "ملفي الشخصي" : "My profile";
  const labelDashboard = isRTL ? "لوحة التحكم" : "Dashboard";
  const labelLogout = isRTL ? "تسجيل الخروج" : "Sign out";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "glass-nav border-b border-white/5" : ""}`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <a
          href="#top"
          data-testid="brand-mark"
          aria-label={t.brand.name}
          className="group block"
        >
          <img
            src={logoAlbaker}
            alt={t.brand.name}
            className="h-12 sm:h-14 w-auto select-none transition-all duration-500 group-hover:brightness-0 group-hover:invert-[62%] group-hover:sepia-[80%] group-hover:saturate-[520%] group-hover:hue-rotate-[358deg]"
            draggable="false"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-10" aria-label="Primary">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className="text-xs tracking-[0.25em] uppercase text-white/60 hover:text-gold transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* Auth: Sign-in or User dropdown */}
          {user ? (
            <div ref={menuRef} className="relative">
              <button
                type="button"
                onClick={() => setMenu((v) => !v)}
                data-testid="user-menu-toggle"
                className="inline-flex items-center gap-2.5 border border-white/15 hover:border-gold hover:text-gold text-bone text-xs tracking-[0.15em] uppercase px-3 sm:px-4 py-2 transition-colors duration-300"
                aria-haspopup="menu"
                aria-expanded={menu}
              >
                <span className="grid place-items-center w-6 h-6 rounded-full bg-gold/15 text-gold text-[11px] font-bold">
                  {initials}
                </span>
                <span className={`hidden sm:inline max-w-[120px] truncate ${isRTL ? "font-arabic" : ""}`}>
                  {user.name || user.email}
                </span>
              </button>

              {menu && (
                <div
                  role="menu"
                  data-testid="user-menu-dropdown"
                  className={`absolute mt-3 w-56 glass-nav border border-white/10 shadow-xl ${isRTL ? "left-0" : "right-0"}`}
                >
                  <div className="px-4 py-3 border-b border-white/10">
                    <div className={`text-bone text-sm truncate ${isRTL ? "font-arabic" : ""}`}>{user.name}</div>
                    <div className="text-white/45 text-xs truncate" dir="ltr">{user.email}</div>
                  </div>
                  {user.role === "admin" && (
                    <Link
                      to="/admin"
                      onClick={() => setMenu(false)}
                      data-testid="user-menu-admin"
                      className={`flex items-center gap-3 px-4 py-3 text-sm text-white/75 hover:bg-white/5 hover:text-gold transition-colors ${isRTL ? "font-arabic" : ""}`}
                    >
                      <LayoutDashboard size={14} strokeWidth={1.5} />
                      <span>{labelDashboard}</span>
                    </Link>
                  )}
                  <Link
                    to="/account"
                    onClick={() => setMenu(false)}
                    data-testid="user-menu-profile"
                    className={`flex items-center gap-3 px-4 py-3 text-sm text-white/75 hover:bg-white/5 hover:text-gold transition-colors ${isRTL ? "font-arabic" : ""}`}
                  >
                    <UserCircle size={14} strokeWidth={1.5} />
                    <span>{labelProfile}</span>
                  </Link>
                  <button
                    type="button"
                    onClick={onLogout}
                    data-testid="user-menu-logout"
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm text-white/75 hover:bg-white/5 hover:text-red-400 transition-colors border-t border-white/10 ${isRTL ? "font-arabic" : ""}`}
                  >
                    <LogOut size={14} strokeWidth={1.5} />
                    <span>{labelLogout}</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="header-login-link"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-gold hover:text-gold text-bone text-xs tracking-[0.2em] uppercase px-3 sm:px-4 py-2 transition-colors duration-300"
              aria-label={labelLogin}
            >
              <User size={14} strokeWidth={1.5} />
              <span className={`hidden sm:inline ${isRTL ? "font-arabic tracking-normal" : ""}`}>{labelLogin}</span>
            </Link>
          )}

          <button
            type="button"
            onClick={toggleLang}
            data-testid="language-toggle"
            className="inline-flex items-center gap-2 border border-white/15 hover:border-gold hover:text-gold text-bone text-xs tracking-[0.2em] uppercase px-3 sm:px-4 py-2 transition-colors duration-300"
            aria-label="Toggle language"
          >
            <Languages size={14} strokeWidth={1.5} />
            <span className={lang === "ar" ? "font-sans" : "font-arabic"}>{t.nav.language}</span>
          </button>

          <button
            type="button"
            className="lg:hidden text-bone p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass-nav border-t border-white/5" data-testid="mobile-menu">
          <nav className="container-x py-6 flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-sm tracking-[0.2em] uppercase text-bone hover:text-gold transition-colors"
              >
                {l.label}
              </a>
            ))}
            {!user && (
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                data-testid="mobile-login-link"
                className="text-sm tracking-[0.2em] uppercase text-gold hover:text-gold-light"
              >
                {labelLogin}
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
